import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Endpoints, Verbiage } from '../../constants';
import { debounce } from '../../helpers/debounce';
import { getSelectedContractNumbers } from '../../helpers/component-helper-methods';
import smartHttp from '../../helpers/smartHttp';
import AgGridComponent from '../../sharedcomponents/ag-grid/AgGrid';
import {
  getFilterColumns,
  getGroupByColumns,
  isGroupBy,
  getSortColumns,
  resizeColumnAsPerValue,
} from '../../sharedcomponents/ag-grid/ag-grid-helper';
import { ReactComponent as ExportIcon } from './../../assets/icons/common/export.svg';

const SalesforceEntitlementsServerside = ({ contracts }) => {
  const gridRef = useRef();
  const sfRowDataRef = useRef([]);
  const navigate = useNavigate();
  const searchableColumns = [
    'emailId',
    'contactname',
    'officeLocationName',
    'packagename',
    'productname',
    'deliveryplatform',
    'pueStatus',
  ];

  const defaultConfig = {
    editable: false,
    isExportCSV: false,
    isExportExcel: false,
    pivotPanelShow: '',
    pagination: false,
    isAutoSizeColums: true,
    enableCharts: false,
    sideBar: true,
    suppressColumnVirtualisation: true,
    overlayNoRowsTemplate:
      contracts && contracts.some((x) => x.checked)
        ? Verbiage.entitlements
        : Verbiage.noData,
  };

  const columnFilterParams = {
    filter: 'agTextColumnFilter',
    enableRowGroup: true,
    enablePivot: false,
    menuTabs: ['generalMenuTab', 'filterMenuTab', 'columnsMenuTab'],
    enableValue: false,
    initialFlex: 1,
  };

  const defaultColdef = {
    ...columnFilterParams,
    editable: false,
    sortable: true,
    resizable: true,
    wrapHeaderText: true,
    autoHeaderHeight: true,
    menuTabs: ['generalMenuTab', 'filterMenuTab'],
  };

  const customValueGetter = (params, columnId) => {
    let newValue = '';
    if (params) {
      let colId = columnId ?? params.column?.colId;
      switch (colId) {
        case 'contactname':
          if (
            params.data?.contactname ||
            (params.data?.firstName && params.data?.lastName)
          ) {
            newValue =
              params.data?.contactname ??
              params.data?.firstName + ' ' + params.data?.lastName;
          }
          break;
        case 'deliveryplatform':
          if (params.data?.deliveryplatform) {
            newValue = params.data.deliveryplatform;
          } else if (
            params.data?.distributor &&
            params.data?.distributorPlatform &&
            params.data?.deliveryType
          ) {
            newValue =
              params.data.distributor +
              ' : ' +
              params.data.distributorPlatform +
              (params.data.deliveryType?.toLowerCase() !== 'web'
                ? ' (' + params.data.deliveryType + ')'
                : '');
          }
          break;
        case 'pueStatus':
          newValue = params.data?.pueStatus ?? params.data?.status;
          break;
        default:
          newValue = params.data?.[colId];
      }
    }
    return newValue;
  };

  const columns = [
    {
      headerName: '',
      field: 'contractNumber',
      resizable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: [
          'inRange',
          {
            displayKey: 'inRange',
            displayName: 'Range Between',
            predicate: ([filterValue], cellValue) => {
              return cellValue == null || cellValue < filterValue;
            },
          },
        ],
      },
      suppressHeaderMenuButton: true,
      sortable: false,
      hide: true,
      suppressColumnsToolPanel: true,
    },
    {
      headerName: 'Email ID',
      headerTooltip: 'Email ID',
      field: 'emailId',
      tooltipField: 'emailId',
    },
    {
      headerName: 'Name',
      headerTooltip: 'Name',
      field: 'contactname',
      valueGetter: customValueGetter,
      tooltipValueGetter: customValueGetter,
      tooltipField: 'contactname',
    },
    {
      headerName: 'Location',
      headerTooltip: 'Location',
      field: 'officeLocationName',
      tooltipField: 'officeLocationName',
      maxWidth: 320,
    },
    {
      headerName: 'Package',
      headerTooltip: 'Package',
      field: 'packagename',
      tooltipField: 'packagename',
    },
    {
      headerName: 'Product',
      headerTooltip: 'Product',
      field: 'productname',
      tooltipField: 'productname',
      maxWidth: 320,
    },
    {
      headerName: 'Delivery Platform (Type)',
      headerTooltip: 'Delivery Platform (Type)',
      field: 'deliveryplatform',
      maxWidth: 320,
      valueGetter: customValueGetter,
      tooltipValueGetter: customValueGetter,
    },
    {
      headerName: 'Status',
      headerTooltip: 'Status',
      field: 'pueStatus',
      tooltipValueGetter: customValueGetter,
      tooltipField: 'pueStatus',
      maxWidth: 225,
    },
  ];

  const getGroupedHeaderName = (field) => {
    const column = columns.find((col) => col.field === field);
    return column?.headerName || field || 'Group';
  };

  const autoGroupColumnDef = {
    ...columnFilterParams,
    enableRowGroup: false,
    minWidth: 220,
    headerValueGetter: (params) => {
      const groupedField = params?.colDef?.showRowGroup;
      return getGroupedHeaderName(groupedField);
    },
  };

  const escapeFilterValue = (value = '') => {
    return String(value).replace(/"/g, '\\"');
  };

  const getAllFilterModel = (colId, newModel) => {
    let currentModel = gridRef?.current?.api?.getFilterModel() || {};
    currentModel[colId] = newModel;
    return currentModel;
  };

  const contractChangeHandler = () => {
    if (gridRef.current && contracts && contracts.length > 0) {
      const { contractNumbers, contractNumberFilter } =
        getSelectedContractNumbers(contracts);
      let commonFilterType = {
        filterType: 'text',
        type: 'inRange',
      };
      let filterValue = {
        filter:
          contractNumbers.length !== 0
            ? contractNumberFilter
            : 'NoContractSelected',
        ...commonFilterType,
      };

      let newFilterModel = getAllFilterModel('contractNumber', filterValue);
      gridRef?.current?.api?.setFilterModel(newFilterModel);
    }
  };

  const getSearchFilter = (searchText) => {
    if (!searchText) {
      return '';
    }
    const escapedSearchText = escapeFilterValue(searchText);
    return `(${searchableColumns
      .map((columnName) => `${columnName}:"*${escapedSearchText}*"`)
      .join(' OR ')})`;
  };

  const getColumnFilterClause = (agParams) => {
    return getFilterColumns({
      filterModel: agParams?.filterModel || {},
      rowGroupCols: agParams?.rowGroupCols || [],
      groupKeys: agParams?.groupKeys || [],
    });
  };

  const buildFinalFilter = (agParams, searchText) => {
    const searchFilter = getSearchFilter(searchText);
    const columnFilterClause = getColumnFilterClause(agParams);

    return [columnFilterClause, searchFilter]
      .filter((item) => !!item)
      .join(' AND ');
  };

  useEffect(() => {
    const reduceCallback = setTimeout(() => {
      contractChangeHandler();
    }, 1000);

    return () => clearTimeout(reduceCallback);
  }, [contracts]);

  const onFilterTextBoxChanged = useCallback(({ target }) => {
    let searchText = target.value.trim();
    let moreThan2 = searchText && searchText.length > 2;
    if (!searchText || moreThan2) {
      gridRef?.current?.api?.onFilterChanged();
    }

    let isRowExist = gridRef?.current?.api?.getRenderedNodes()?.length > 0;
    if (moreThan2 && !isRowExist) {
      gridRef?.current?.api?.showNoRowsOverlay();
    } else {
      gridRef?.current?.api?.hideOverlay();
    }
  }, []);

  const onFilterTextBoxChangedDebounce = debounce(onFilterTextBoxChanged, 1000);

  const getServerSideDatasource = () => {
    return {
      getRows: (params) => {
        try {
          let AgParams = params?.request;
          let groupedColumn = getGroupByColumns(AgParams);
          let contractFilterModel =
            AgParams?.filterModel?.contractNumber?.filter;

          if (
            !contractFilterModel ||
            contractFilterModel === 'NoContractSelected'
          ) {
            params.success({
              rowData: [],
              rowCount: 0,
            });

            if (contractFilterModel === 'NoContractSelected') {
              gridRef?.current?.api?.showNoRowsOverlay();
            }
            return;
          }

          let searchText = document
            .getElementById('salesforce-entitlement-search-filter-input')
            .value.trim();
          const finalFilter = buildFinalFilter(AgParams, searchText);

          if (finalFilter) {
            let queryParams = {
              Filter: finalFilter,
              Field: isGroupBy(AgParams)
                ? `count(${groupedColumn})`
                : 'userEntitlementId,contractNumber,emailId,contactname,firstName,lastName,officeLocationName,packagename,productname,deliveryplatform,pueStatus',
              Sort: getSortColumns(AgParams, 'emailId:asc'),
              PageSize: AgParams.endRow - AgParams.startRow,
              Page:
                AgParams.startRow / (AgParams.endRow - AgParams.startRow) + 1,
              groupBy: groupedColumn,
              id: String(Date.now()),
            };

            gridRef?.current?.api?.showLoadingOverlay();
            smartHttp
              .getRapid(Endpoints.salesforceEntitlementApi, queryParams)
              .then(({ data }) => {
                if (data) {
                  let rowData = data?.results ?? data?.aggResultValue;

                  if (
                    data?.aggResultValue &&
                    data?.aggResultValue?.length > 0
                  ) {
                    // On groupby - create key context to avoid duplicate grouped rows.
                    let groupKeys = AgParams.groupKeys.join(',');
                    rowData = rowData.map((el) => {
                      return { ...el, groupKeys: groupKeys };
                    });
                  }

                  sfRowDataRef.current = rowData;
                  params.success({
                    rowData: rowData,
                    rowCount: data?.metadata.count,
                  });

                  if (rowData?.length === 0)
                    gridRef?.current?.api?.showNoRowsOverlay();
                  else {
                    gridRef?.current?.api?.hideOverlay();
                  }
                }
              })
              .catch(() => {
                params.fail();
                gridRef?.current?.api?.hideOverlay();
              });
          } else {
            params.success({
              rowData: [],
              rowCount: 0,
            });
            gridRef?.current?.api?.showNoRowsOverlay();
          }
        } catch (err) {
          params.fail();
        }
      },
    };
  };

  const onGridReadyHandler = (params) => {
    gridRef.current = params;
    params.api.setGridOption('serverSideDatasource', getServerSideDatasource());
    contractChangeHandler();
  };

  const onFirstDataRenderedHandler = (params) => {
    resizeColumnAsPerValue(params, sfRowDataRef.current);
  };

  const onGridSizeChangedHandler = (params) => {
    requestAnimationFrame(() => {
      resizeColumnAsPerValue(params, sfRowDataRef.current);
    });
  };

  const onColumnVisibleHandler = (params) => {
    requestAnimationFrame(() => {
      resizeColumnAsPerValue(params, sfRowDataRef.current);
    });
  };

  const getChildCount = useCallback((data) => {
    for (const key in data) {
      if (key.startsWith('count_')) {
        return data[key] ?? 0;
      }
    }
    return 0;
  }, []);

  const getRowId = (params) => {
    // Keep row IDs stable for both grouped and flat rows.
    return Object.entries(params?.data ?? {}).join(',');
  };

  const handleExportReport = () => {
    navigate('/reports', { state: { preselectSource: 'SFDC' } });
  };

  return (
    <>
      <div className="row mt-2">
        <div className="col">
          <input
            type="text"
            autoFocus
            id="salesforce-entitlement-search-filter-input"
            className="form-control search-box"
            placeholder="Search by users, packages, products, delivery platform or status"
            onInput={onFilterTextBoxChangedDebounce}
          ></input>
        </div>
        <div className="col action-item-bar">
          <span className="user-grid-icons icon-divider">
            <ExportIcon
              className="icon-active"
              alt="Export Active Users Report"
              width="18"
              title="Export Active Users Report"
              onClick={handleExportReport}
            />
          </span>
        </div>
      </div>
      <div className="ag-grid-table salesforce-tab-grid mb-2">
        <AgGridComponent
          autoGroupColumnDef={autoGroupColumnDef}
          config={defaultConfig}
          defaultColumnDef={defaultColdef}
          columns={columns}
          getChildCount={getChildCount}
          getRowId={getRowId}
          onGridReady={onGridReadyHandler}
          onFirstDataRendered={onFirstDataRenderedHandler}
          onGridSizeChanged={onGridSizeChangedHandler}
          onColumnVisible={onColumnVisibleHandler}
        />
        <div className="notes-text-label mt-1" id="notes-label">
          The entitlements are read-only. For any changes reach out to Energy
          Support.
        </div>
      </div>
    </>
  );
};

export default SalesforceEntitlementsServerside;
